import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { EventsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { format } from 'date-fns'
import { StateBadge } from './badge'
import { Badge as CategoryBadge } from '@/components/category/badge'
import { Avatar } from '../base/avatar'
import { EventFilters, EventOrder, SortOrder } from '@/types'
import { formatCurrency } from '@/helpers/formatCurrency'
import { Button } from '@/components/base/button'
import { ShowDialog } from './show_dialog'
import { dedupe } from '@/helpers/dedupe'
import { useTable } from '@/helpers/useTable'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  filters?: EventFilters
  createable?: boolean
}

export const Table = ({ pageSize = 20, filters = {}, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<EventsQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.events?.pageInfo, onIndex, basePath: '/events' })

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<EventFilters, EventOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ dateOut: SortOrder.Desc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.events.nodes = dedupe([
              ...previousData.events.nodes,
              ...fetchMoreResult.events.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, descriptionCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, clientOrganisationNameCont: value })),
            type: 'text',
            placeholder: 'Who was it for?'
          },
          {
            index: 7,
            setValue: (set, value) => set(f => ({ ...f, stateEq: value })),
            type: 'select',
            options: [
              { value: 'pending', display: <StateBadge event={{ state: 'pending' }} /> },
              { value: 'confirmed', display: <StateBadge event={{ state: 'confirmed' }} /> },
              { value: 'cancelled', display: <StateBadge event={{ state: 'cancelled' }} /> }
            ]
          }
        ]}
        headings={[
          { name: 'Description' },
          { name: 'Client' },
          { name: 'Organisers' },
          { name: 'Out', sortField: 'dateOut', defaultDirection: SortOrder.Desc },
          { name: 'Return', sortField: 'dateReturn', defaultDirection: SortOrder.Desc },
          { name: 'Value' },
          { name: '' },
          { name: 'State' },
        ]}
        rows={data.events.nodes.map(event => (
          {
            id: event.id,
            columns: [
              event.description,
              event.client.organisationName,
              (
                <div>
                  {
                    event.organisers.map(o => o.avatar
                      ? <Avatar key={o.id} className="size-6" src={o.avatar.url} square />
                      : <span />)
                  }
                </div>
              ),
              format(event.dateOut, 'KK:mm dd/MM/yyyy'),
              format(event.dateReturn, 'KK:mm dd/MM/yyyy'),
              formatCurrency(event.currency, event.totalCents),
              (
                <div className="flex flex-wrap gap-2">
                  { event.categories.map(c => <CategoryBadge key={c.id} category={c} />) }
                </div>
              ),
              <StateBadge event={event} />
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
